import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { TWCIcon, TWCText } from '@components';
import { useRecoilValue } from 'recoil';

import { dailyDaypartForecastTestIDs, Theme } from '@constants';
import { dayDateTimeState } from '@store';
import { IconCode, MoonIconCode } from '@types';

import { DaypartDay } from './DaypartDay/DaypartDay';
import { DaypartNight } from './DaypartNight/DaypartNight';

import { accessibleTime, isDisplayable, missingValue } from '../../../helpers/StringUtils';
import { TimeUtils } from '../../../helpers/TimeUtils';

type Day = {
  isDaySection: true;
  sunriseTimeUtc: number;
  sunsetTimeUtc: number;
  moonPhase: undefined;
  moonPhaseCode: undefined;
};

type Night = {
  isDaySection: false;
  moonPhase: string;
  moonPhaseCode: MoonIconCode;
  sunriseTimeUtc: undefined;
  sunsetTimeUtc: undefined;
};

type DailyDaypartForecastProps = (Day | Night) & {
  daypartName: string;
  iconCodeExtend: IconCode;
  narrative: string;
  temperature?: number;
  isLoading?: boolean;
  error?: unknown;
};

export const DailyDaypartForecast: FC<DailyDaypartForecastProps> = ({
  daypartName,
  isDaySection,
  iconCodeExtend,
  narrative,
  moonPhase,
  moonPhaseCode,
  temperature,
  sunriseTimeUtc,
  sunsetTimeUtc,
  isLoading = false,
  error,
  ...restProperties
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'dailyWeatherForecastDaypart',
  });

  const { timeZone } = useRecoilValue(dayDateTimeState);

  const timeFormat = TimeUtils.getTimeFormat();
  const testIDs = dailyDaypartForecastTestIDs;

  const sunriseTime =
    error || !sunriseTimeUtc || !timeZone
      ? '-'
      : TimeUtils.getZonedTime(TimeUtils.dateFromEpoch(sunriseTimeUtc), timeZone, timeFormat);

  const sunsetTime =
    error || !sunsetTimeUtc || !timeZone
      ? '-'
      : TimeUtils.getZonedTime(TimeUtils.dateFromEpoch(sunsetTimeUtc), timeZone, timeFormat);

  const accessibleSunriseTime = accessibleTime(sunriseTime);
  const accessibleSunsetTime = accessibleTime(sunsetTime);

  return (
    <View
      accessibilityHint={t('accessibilityHint')}
      accessibilityLabel={
        error
          ? t('errorAccessibilityLabel')
          : isDaySection
          ? t('dayAccessibilityLabel', {
              day: daypartName,
              sunriseTime: accessibleSunriseTime,
              sunsetTime: accessibleSunsetTime,
              temperature,
              narrative,
            })
          : t('nightAccessibilityLabel', {
              night: daypartName,
              moonPhase,
              temperature,
              narrative,
            })
      }
      style={styles.container}
      {...restProperties}
    >
      {isLoading ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator color={Theme.loading} size={'large'} />
        </View>
      ) : (
        <View importantForAccessibility={'no-hide-descendants'}>
          <View style={styles.topRow}>
            {isDaySection ? (
              <DaypartDay daypartName={daypartName} sunriseTime={sunriseTime} sunsetTime={sunsetTime} t={t} />
            ) : (
              <DaypartNight daypartName={daypartName} moonPhaseCode={moonPhaseCode} t={t} moonPhase={moonPhase} />
            )}
          </View>
          <View style={styles.bottomRow}>
            {error ? (
              <View>
                <TWCText noTestID variant={'regularText80'}>
                  {'—'}
                </TWCText>
              </View>
            ) : (
              <>
                <TWCIcon
                  code={iconCodeExtend}
                  height={78}
                  testID={isDaySection ? testIDs.weatherIconDay : testIDs.weatherIconNight}
                  width={78}
                />
                <TWCText
                  style={styles.tempText}
                  testID={isDaySection ? testIDs.tempuratureTextDay : testIDs.tempuratureTextNight}
                  variant={'regularText80'}
                >
                  {isDisplayable(temperature, error) ? `${temperature}˚` : missingValue}
                </TWCText>
                <TWCText
                  numberOfLines={5}
                  style={styles.forecastText}
                  testID={isDaySection ? testIDs.forecastTextDay : testIDs.forecastTextNight}
                  variant={'regularText22'}
                >
                  {narrative}
                </TWCText>
              </>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bottomRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    marginTop: Theme.px(48),
  },
  centerContainer: {
    alignItems: 'center',
    height: Theme.px(280),
    justifyContent: 'center',
    width: Theme.px(802),
  },
  container: {
    flexDirection: 'column',
    height: Theme.px(280),
    width: Theme.px(802),
  },
  forecastText: {
    lineHeight: Theme.px(34),
    marginLeft: Theme.px(86),
    width: Theme.px(480),
  },
  tempText: {
    marginLeft: Theme.px(38),
  },
  topRow: {
    alignItems: 'center',
    flexDirection: 'row',
    height: Theme.px(38),
    justifyContent: 'flex-start',
    width: '100%',
  },
});
